import React, { useState, useEffect } from 'react';
import { Card, CardContent, Typography, Box, Chip, Slider, Switch, FormControlLabel, Tooltip, CircularProgress } from '@mui/material';
import { Link as LinkIcon, Warning, CheckCircle } from '@mui/icons-material';
import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';
const API_BASE = `${API_URL}/api/v1`;

/**
 * LinkingVisualization Component
 * 
 * Network graph showing rotation ↔ timecard connections with confidence scores,
 * link methods, and special highlighting for spillover rotations (linked to 2+ timecards).
 * 
 * @param {string} sourceType - "rotation" or "timecard"
 * @param {number} sourceVersionId - Version ID of the source entity
 * @param {Object} sourceMetadata - Metadata about source (rotation_number, etc.)
 * @param {function} onNavigate - Callback when clicking a linked entity (optional)
 */
const LinkingVisualization = ({ sourceType, sourceVersionId, sourceMetadata, onNavigate }) => {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [confidenceThreshold, setConfidenceThreshold] = useState(0.0);
  const [showDetails, setShowDetails] = useState(true);
  const [hoveredLink, setHoveredLink] = useState(null);

  // Fetch linked versions from API
  useEffect(() => {
    const fetchLinks = async () => {
      setLoading(true);
      setError(null);
      
      try {
        let response;
        if (sourceType === 'rotation') {
          // Endpoint 1: Get timecards for rotation version
          response = await axios.get(`${API_BASE}/version-links/rotation-version/${sourceVersionId}/timecards`);
        } else {
          // Endpoint 2: Get rotations for timecard version
          response = await axios.get(`${API_BASE}/version-links/timecard-version/${sourceVersionId}/rotations`);
        }
        
        setLinks(response.data || []);
      } catch (err) {
        console.error('Failed to fetch version links:', err);
        setError(err.message || 'Failed to load linking data');
      } finally {
        setLoading(false);
      }
    };

    if (sourceVersionId) {
      fetchLinks();
    }
  }, [sourceType, sourceVersionId]);

  // Filter links by confidence threshold
  const filteredLinks = links.filter(link => link.link_confidence >= confidenceThreshold);

  // Check if spillover (rotation linked to 2+ timecards)
  const isSpillover = sourceType === 'rotation' && filteredLinks.length >= 2;

  // Get confidence color
  const getConfidenceColor = (confidence) => {
    if (confidence >= 0.8) return '#4caf50'; // Green
    if (confidence >= 0.6) return '#ff9800'; // Orange
    return '#f44336'; // Red
  };

  // Get link method icon/color
  const getLinkMethodBadge = (method) => {
    switch (method) {
      case 'auto':
        return { label: 'AUTO', color: 'primary', icon: <CheckCircle fontSize="small" /> };
      case 'spillover':
        return { label: 'SPILLOVER', color: 'warning', icon: <Warning fontSize="small" /> };
      case 'manual':
        return { label: 'MANUAL', color: 'secondary', icon: <LinkIcon fontSize="small" /> };
      default:
        return { label: method?.toUpperCase() || 'UNKNOWN', color: 'default', icon: null };
    }
  };

  // Handle entity click
  const handleEntityClick = (link) => {
    if (onNavigate) {
      const targetType = sourceType === 'rotation' ? 'timecard' : 'rotation';
      const targetId = sourceType === 'rotation' ? link.timecard_version_id : link.rotation_version_id;
      const targetNumber = sourceType === 'rotation' ? link.timecard_number : link.rotation_number;
      
      onNavigate({
        type: targetType,
        versionId: targetId,
        number: targetNumber,
        employeeId: link.employee_id
      });
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent>
          <Typography variant="body2" color="error">
            Error: {error}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  if (links.length === 0) {
    return (
      <Card>
        <CardContent>
          <Typography variant="body2" color="text.secondary">
            No linked versions found for this {sourceType}.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Box>
      {/* Controls */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Confidence threshold slider */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Confidence Threshold: {(confidenceThreshold * 100).toFixed(0)}%
              </Typography>
              <Slider
                value={confidenceThreshold}
                onChange={(e, newValue) => setConfidenceThreshold(newValue)}
                min={0}
                max={1}
                step={0.1}
                marks={[
                  { value: 0, label: '0%' },
                  { value: 0.6, label: '60%' },
                  { value: 0.8, label: '80%' },
                  { value: 1, label: '100%' }
                ]}
                valueLabelDisplay="auto"
                valueLabelFormat={(value) => `${(value * 100).toFixed(0)}%`}
              />
              <Typography variant="caption" color="text.secondary">
                Showing {filteredLinks.length} of {links.length} link{links.length !== 1 ? 's' : ''}
              </Typography>
            </Box>

            {/* Show details toggle */}
            <FormControlLabel
              control={
                <Switch
                  checked={showDetails}
                  onChange={(e) => setShowDetails(e.target.checked)}
                />
              }
              label="Show link details"
            />
          </Box>
        </CardContent>
      </Card>

      {/* Spillover alert */}
      {isSpillover && (
        <Card sx={{ mb: 2, bgcolor: 'warning.light', borderLeft: 4, borderColor: 'warning.main' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Warning color="warning" />
              <Typography variant="subtitle2">
                SPILLOVER ROTATION: This rotation is linked to {filteredLinks.length} timecards (crosses bid period boundary)
              </Typography>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Network visualization */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* Source node (center) */}
        <Card sx={{ 
          bgcolor: 'primary.light', 
          border: 2, 
          borderColor: 'primary.main',
          boxShadow: 3 
        }}>
          <CardContent>
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LinkIcon />
              {sourceType === 'rotation' ? 'Rotation' : 'Timecard'} {sourceMetadata?.number || sourceVersionId}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Source (Version {sourceMetadata?.version_number || 'N/A'})
            </Typography>
          </CardContent>
        </Card>

        {/* Linked nodes */}
        {filteredLinks.map((link, index) => {
          const confidence = link.link_confidence;
          const methodBadge = getLinkMethodBadge(link.link_method);
          const isHovered = hoveredLink === link.id;
          
          return (
            <Box key={link.id} sx={{ position: 'relative' }}>
              {/* Connection line visualization */}
              <Box
                sx={{
                  position: 'absolute',
                  left: '50%',
                  top: -16,
                  width: 2,
                  height: 16,
                  bgcolor: getConfidenceColor(confidence),
                  opacity: 0.6
                }}
              />
              
              <Card
                sx={{
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  transform: isHovered ? 'scale(1.02)' : 'scale(1)',
                  border: isHovered ? 2 : 1,
                  borderColor: isHovered ? 'primary.main' : 'divider',
                  bgcolor: link.link_method === 'spillover' ? 'warning.lighter' : 'background.paper'
                }}
                onClick={() => handleEntityClick(link)}
                onMouseEnter={() => setHoveredLink(link.id)}
                onMouseLeave={() => setHoveredLink(null)}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 1 }}>
                    <Box>
                      <Typography variant="subtitle1">
                        {sourceType === 'rotation' ? 'Timecard' : 'Rotation'} {sourceType === 'rotation' ? link.timecard_number : link.rotation_number}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Version {sourceType === 'rotation' ? link.timecard_version_number : link.rotation_version_number}
                      </Typography>
                    </Box>
                    
                    {/* Confidence bar */}
                    <Tooltip title={`${(confidence * 100).toFixed(0)}% confidence`}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box
                          sx={{
                            width: 60,
                            height: 8,
                            bgcolor: 'grey.200',
                            borderRadius: 1,
                            overflow: 'hidden'
                          }}
                        >
                          <Box
                            sx={{
                              width: `${confidence * 100}%`,
                              height: '100%',
                              bgcolor: getConfidenceColor(confidence),
                              transition: 'width 0.3s'
                            }}
                          />
                        </Box>
                        <Typography variant="caption" sx={{ minWidth: 35, textAlign: 'right' }}>
                          {(confidence * 100).toFixed(0)}%
                        </Typography>
                      </Box>
                    </Tooltip>
                  </Box>

                  {/* Link method badge */}
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Chip
                      icon={methodBadge.icon}
                      label={methodBadge.label}
                      size="small"
                      color={methodBadge.color}
                    />
                    
                    {link.link_method === 'spillover' && link.link_details?.period && (
                      <Chip
                        label={link.link_details.period}
                        size="small"
                        variant="outlined"
                        color="warning"
                      />
                    )}
                  </Box>

                  {/* Detailed link info (collapsible) */}
                  {showDetails && (
                    <Box sx={{ mt: 2, pt: 1, borderTop: 1, borderColor: 'divider' }}>
                      {link.link_reason && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          Reason: {link.link_reason}
                        </Typography>
                      )}
                      <Typography variant="caption" color="text.secondary" display="block">
                        Created: {new Date(link.created_at).toLocaleDateString()}
                      </Typography>
                      {link.created_by && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          By: {link.created_by}
                        </Typography>
                      )}
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Box>
          );
        })}
      </Box>

      {/* Summary footer */}
      <Card sx={{ mt: 2, bgcolor: 'action.hover' }}>
        <CardContent>
          <Typography variant="caption" color="text.secondary">
            {filteredLinks.length} linked version{filteredLinks.length !== 1 ? 's' : ''} • 
            Employee: {filteredLinks[0]?.employee_id || 'N/A'}
            {isSpillover && ' • SPILLOVER DETECTED'}
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
};

export default LinkingVisualization;
